import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag } from 'antd';
import moment from 'moment';
import { getAuditTrailLogs } from '../../services/auditTrailService';
import AuditDetailModal from './AuditDetailModal';
import { parseChannel, channelTagColor } from './utils';

/**
 * Login Activity Tab
 * Specifically tracks USER_LOGIN events across the platform.
 */
const LoginAuditTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const fetchLogs = useCallback(async (page = 1, size = 10) => {
    setLoading(true);
    try {
      // Default view: Fetch logs and filter for USER_LOGIN
      const res = await getAuditTrailLogs({ page: page - 1, size: 100 });
      if (res?.success) {
        const allContent = res.data?.content || [];
        const loginEvents = allContent.filter((e) => e.eventType === 'USER_LOGIN');
        
        const start = (page - 1) * size;
        const pageContent = loginEvents.slice(start, start + size);
        
        setLogs(pageContent);
        setPagination({ current: page, pageSize: size, total: loginEvents.length });
      } else {
        setLogs([]);
      }
    } catch (err) {
      console.error('Failed to fetch login audit logs:', err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleTableChange = (pag) => fetchLogs(pag.current, pag.pageSize);

  const handleRowClick = (record) => {
    setSelectedLog(record);
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
      render: (id) => <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{id}</span>,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (val) => <Tag color="blue">{val || 'Login'}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const isSuccess = status === 'SUCCESS';
        return (
          <Tag color={isSuccess ? 'success' : 'error'} style={{ fontWeight: 700, borderRadius: 20 }}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'Channel',
      dataIndex: 'userAgent',
      key: 'channel',
      render: (ua) => {
        const channel = parseChannel(ua);
        return (
          <Tag color={channelTagColor(channel)} style={{ fontWeight: 600, borderRadius: 6, fontSize: 11 }}>
            {channel}
          </Tag>
        );
      },
    },
    {
      title: 'IP Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      render: (ip) => <span style={{ fontFamily: 'monospace', color: '#595959' }}>{ip || '—'}</span>,
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 190,
      render: (ts) => (
        <span style={{ color: '#8c8c8c', fontSize: 12, fontWeight: 500 }}>
          {ts ? moment(ts).format('MMM DD, YYYY  •  hh:mm A') : '—'}
        </span>
      ),
    },
  ];

  return (
    <div>
      <div className="audit-table-card">
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} records`,
          }}
          onChange={handleTableChange}
          onRow={(record) => ({ onClick: () => handleRowClick(record) })}
          rowClassName={(_, idx) => (idx % 2 === 0 ? 'audit-row-even' : 'audit-row-odd')}
          scroll={{ x: 'max-content' }}
          className="audit-table home-table"
        />
      </div>
      <AuditDetailModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        record={selectedLog}
      />
    </div>
  );
};

export default LoginAuditTab;
